import { LitElement } from 'lit';

type Constructor<T = object> = new (...args: any[]) => T; // eslint-disable-line @typescript-eslint/no-explicit-any

export declare class CompVisibleMixinI {
  installIntersectionObserver(): void;
  removeIntersectionObserver(): void;
  componentBecameVisible(): void;
  intersectionObserverHandler(entries: IntersectionObserverEntry[]): void;
}

/**
 * This mixin adds a component visibility check to a given component:
 * If the component is "visible" it executes a custom lifecycle method.
 * "visible" in this case means that the component tag is rendered on the page.
 * It is "visible" even if it does not render anything actually visible to the customer.
 */
export const CompVisibleMixin = <T extends Constructor<LitElement>>(superClass: T) => {
  class CompVisibleMixinClass extends superClass {
    private intersectionObserver?: IntersectionObserver;
    private intersectionObserverInitialSkipDone: boolean = false;

    disconnectedCallback() {
      this.removeIntersectionObserver();
      super.disconnectedCallback();
    }

    /**
     * Install IntersectionObserver to monitor component visibility
     */
    installIntersectionObserver() {
      // Check IntersectionObserver availability
      if (typeof globalThis.IntersectionObserver === 'undefined') {
        return null;
      }

      this.intersectionObserver = new IntersectionObserver((entries) => { this.intersectionObserverHandler(entries); }, {
        root: null, // observes the complete viewport
        threshold: 0.1, // trigger when at least 10% of the component is visible
      });

      // Start observing
      this.intersectionObserver.observe(this);
      return this.intersectionObserver;
    }

    /**
     * removes the IntersectionObserver installed by #installIntersectionObserver
     */
    removeIntersectionObserver() {
      if (this.intersectionObserver) {
        this.intersectionObserver.disconnect();
      }
    }

    intersectionObserverHandler(entries: IntersectionObserverEntry[]) {
      // Skip the initial call since the IntersectionObserver is called once for init
      // See https://developer.mozilla.org/en-US/docs/Web/API/IntersectionObserver/observe
      if (!this.intersectionObserverInitialSkipDone) {
        this.intersectionObserverInitialSkipDone = true;
        return;
      }

      for (const entry of entries) {
        // See https://developer.mozilla.org//en-US/docs/Web/API/Element/checkVisibility
        const isMfeVisible = typeof Element.prototype.checkVisibility === 'undefined' ? true : entry.target.checkVisibility();

        if (entry.isIntersecting && isMfeVisible) {
          this.componentBecameVisible();
          return;
        }
      }
    }

    /**
     * Called when the component becomes visible
     */
    componentBecameVisible() {
      throw new Error('components using the "CompVisibleMixin" mixin must implement "componentBecameVisible()"');
    }
  }

  return CompVisibleMixinClass as Constructor<CompVisibleMixinI> & T;
};
